import React, { useState } from "react";
import Popover from "../../components/ui/Popover";
import Icon from "../../components/ui/Icon";
import { matchesView } from "../../inventory/savedViews";

// The "Views" menu: saved combinations of search, filters, sort and columns.
// Picking one applies it; the one that matches what's on screen is ticked.
export default function SavedViews({ views, current, onApply, onSave, onUpdate, onRename, onDelete }) {
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [renaming, setRenaming] = useState(null); // { id, name }

  const active = views.find((v) => matchesView(v, current.query, current.layout));
  const taken = (n, exceptId) =>
    views.some((v) => v.id !== exceptId && v.name.trim().toLowerCase() === n.trim().toLowerCase());

  const save = (e) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) return setError("Give the view a name.");
    if (taken(n)) return setError(`You already have a view called ${n}.`);
    onSave(n);
    setName("");
    setError("");
    return undefined;
  };

  const finishRename = () => {
    const n = renaming.name.trim();
    if (n && !taken(n, renaming.id)) onRename(renaming.id, n);
    setRenaming(null);
  };

  return (
    <Popover
      triggerClass={`filter-btn views-btn${active ? " is-active" : ""}`}
      trigger={
        <>
          <Icon name="star" size={14} />
          <span className="views-btn-label">{active ? active.name : "Views"}</span>
          <Icon name="chevron" size={14} />
        </>
      }
    >
      {(close) => (
        <div className="views">
          <div className="menu-label">Saved views</div>

          {views.length === 0 ? (
            <p className="views-empty">
              Save the search, filters, sort and columns you use often, and get back to them in one click.
            </p>
          ) : (
            <ul className="views-list">
              {views.map((v) => {
                const isActive = active?.id === v.id;
                return (
                  <li key={v.id} className={isActive ? "is-active" : ""}>
                    {renaming?.id === v.id ? (
                      <input
                        className="form-input views-rename"
                        value={renaming.name}
                        autoFocus
                        aria-label="View name"
                        onChange={(e) => setRenaming({ ...renaming, name: e.target.value })}
                        onBlur={finishRename}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") finishRename();
                          if (e.key === "Escape") {
                            e.stopPropagation();
                            setRenaming(null);
                          }
                        }}
                      />
                    ) : (
                      <button
                        type="button"
                        className="views-apply"
                        onClick={() => {
                          onApply(v);
                          close();
                        }}
                      >
                        <span className="views-check">{isActive && <Icon name="check" size={14} />}</span>
                        <span className="views-name">{v.name}</span>
                      </button>
                    )}
                    <span className="views-tools">
                      {!isActive && (
                        <button
                          type="button"
                          className="icon-btn"
                          title="Replace with what's on screen now"
                          aria-label={`Update ${v.name} with the current filters and columns`}
                          onClick={() => onUpdate(v.id)}
                        >
                          <Icon name="refresh" size={14} />
                        </button>
                      )}
                      <button
                        type="button"
                        className="icon-btn"
                        title="Rename"
                        aria-label={`Rename ${v.name}`}
                        onClick={() => setRenaming({ id: v.id, name: v.name })}
                      >
                        <Icon name="edit" size={14} />
                      </button>
                      <button
                        type="button"
                        className="icon-btn danger"
                        title="Delete"
                        aria-label={`Delete ${v.name}`}
                        onClick={() => onDelete(v.id)}
                      >
                        <Icon name="trash" size={14} />
                      </button>
                    </span>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="menu-sep" />
          <form className="views-save" onSubmit={save}>
            <input
              className={`form-input${error ? " has-error" : ""}`}
              placeholder={active ? "Save as a new view" : "Name this view"}
              aria-label="New view name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError("");
              }}
            />
            <button type="submit" className="btn-primary">
              Save
            </button>
          </form>
          {error && <div className="form-error views-error">{error}</div>}
        </div>
      )}
    </Popover>
  );
}
